import React, { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Shield, Users, Target, Wallet, Check, X, AlertCircle, Trophy, Clock,
  Eye, TrendingUp, DollarSign, FileText, MessageSquare, Bot, RefreshCw,
  Trash2, Pause, CreditCard, Activity, BarChart3, Download, Search, Flag,
  UserX, UserCheck, ChevronDown, ChevronRight, Bell, Globe, Award, Mail, Brain
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'bets', label: 'Bets', icon: Target },
  { id: 'transactions', label: 'Transactions', icon: DollarSign },
  { id: 'rps', label: 'RPS Games', icon: Trophy },
  { id: 'kyc', label: 'KYC', icon: UserCheck },
  { id: 'ai-insights', label: 'AI Insights', icon: Brain },
  { id: 'leaderboard', label: 'Leaderboard', icon: Award },
  { id: 'audit', label: 'Audit Log', icon: Activity },
  { id: 'contact', label: 'Contact Msgs', icon: Mail },
  { id: 'support', label: 'Support', icon: MessageSquare },
  { id: 'ai-engine', label: 'AI Engine', icon: Bot },
];

const StatCard = ({ icon: Icon, label, value, color = '#D4AF37', sub, onClick }) => (
  <button
    onClick={onClick}
    className={`bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-left w-full transition-all ${onClick ? 'hover:shadow-md hover:border-[#D4AF37] cursor-pointer' : 'cursor-default'}`}
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-2xl font-black text-gray-900 truncate">{value}</div>
        <div className="text-xs text-gray-500 uppercase font-semibold">{label}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
      </div>
      {onClick && <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />}
    </div>
  </button>
);

const StatusPill = ({ status }) => {
  const colors = {
    active: 'bg-green-100 text-green-700',
    open: 'bg-yellow-100 text-yellow-700',
    pending_approval: 'bg-orange-100 text-orange-700',
    resolved: 'bg-blue-100 text-blue-700',
    completed: 'bg-blue-100 text-blue-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-600',
    expired: 'bg-gray-100 text-gray-600',
    expired_refunded: 'bg-gray-100 text-gray-600',
    disputed: 'bg-pink-100 text-pink-700',
    suspended: 'bg-red-100 text-red-700',
    banned: 'bg-red-200 text-red-800',
    approved: 'bg-green-100 text-green-700',
    pending: 'bg-orange-100 text-orange-700',
    new: 'bg-orange-100 text-orange-700',
    read: 'bg-blue-100 text-blue-700',
    replied: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {String(status || '-').replace(/_/g, ' ')}
    </span>
  );
};

const RiskBadge = ({ score }) => {
  const level = score >= 75 ? 'high' : score >= 40 ? 'medium' : 'low';
  const cls = level === 'high' ? 'bg-red-100 text-red-700' : level === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';
  return <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${cls}`} title={`Risk: ${score}`}>{score}</span>;
};

const Countdown = ({ seconds }) => {
  if (seconds == null) return <span className="text-gray-400">-</span>;
  if (seconds <= 0) return <span className="text-red-500">Expired</span>;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return <span className="text-xs font-mono text-gray-600">{h}h {m}m</span>;
};

const downloadBlob = async (promise, filename) => {
  try {
    const res = await promise;
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  } catch (e) {
    toast.error('Export failed');
  }
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Core data
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [geoStats, setGeoStats] = useState([]);
  const [users, setUsers] = useState([]);
  const [bets, setBets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [rpsGames, setRpsGames] = useState([]);
  const [kycList, setKycList] = useState([]);
  const [aiAlerts, setAiAlerts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [audit, setAudit] = useState([]);
  const [contact, setContact] = useState([]);
  const [support, setSupport] = useState([]);

  // Filters
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [userKycFilter, setUserKycFilter] = useState('');
  const [betSearch, setBetSearch] = useState('');
  const [betFilter, setBetFilter] = useState('all');
  const [txSearch, setTxSearch] = useState('');
  const [txFilter, setTxFilter] = useState('');
  const [rpsFilter, setRpsFilter] = useState('');
  const [lbMetric, setLbMetric] = useState('wins');

  // Modals
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendModal, setSuspendModal] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [kycRejectModal, setKycRejectModal] = useState(null);
  const [kycComment, setKycComment] = useState('');
  const [profileModal, setProfileModal] = useState(null);
  const [profileData, setProfileData] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ov, gc, geo, us, be, tx, rp, kc, ai, lb, au, ct, sp] = await Promise.all([
        adminApi.overviewV2().catch(() => ({ data: null })),
        adminApi.growthCharts(30).catch(() => ({ data: null })),
        adminApi.geoStats().catch(() => ({ data: [] })),
        adminApi.usersEnriched({ limit: 500 }).catch(() => ({ data: { users: [] } })),
        adminApi.betsEnriched({ limit: 500 }).catch(() => ({ data: { bets: [] } })),
        adminApi.transactionsEnriched({ limit: 500 }).catch(() => ({ data: { transactions: [] } })),
        adminApi.rpsEnriched({ limit: 200 }).catch(() => ({ data: { games: [] } })),
        adminApi.kycPanel().catch(() => ({ data: [] })),
        adminApi.aiInsights().catch(() => ({ data: { alerts: [] } })),
        adminApi.leaderboard('wins', 20).catch(() => ({ data: [] })),
        adminApi.auditLog(200).catch(() => ({ data: { logs: [] } })),
        adminApi.contactMessages(100).catch(() => ({ data: { messages: [] } })),
        adminApi.getSupportTickets().catch(() => ({ data: [] })),
      ]);
      setOverview(ov.data);
      setGrowth(gc.data);
      setGeoStats(geo.data || []);
      setUsers(us.data.users || []);
      setBets(be.data.bets || []);
      setTransactions(tx.data.transactions || []);
      setRpsGames(rp.data.games || []);
      setKycList(kc.data || []);
      setAiAlerts(ai.data.alerts || []);
      setLeaderboard(lb.data || []);
      setAudit(au.data.logs || []);
      setContact(ct.data.messages || []);
      setSupport(sp.data || []);
    } catch (e) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // When leaderboard metric changes
  useEffect(() => {
    if (activeTab === 'leaderboard') {
      adminApi.leaderboard(lbMetric, 20).then((r) => setLeaderboard(r.data || [])).catch(() => {});
    }
  }, [lbMetric, activeTab]);

  // ----- Filtered Data -----
  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase();
    return users.filter((u) => {
      if (userStatusFilter && u.account_status !== userStatusFilter) return false;
      if (userKycFilter && u.kyc_status !== userKycFilter) return false;
      if (!q) return true;
      return (u.full_name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.mobile_number || '').toLowerCase().includes(q)
        || (u.username || '').toLowerCase().includes(q);
    });
  }, [users, userSearch, userStatusFilter, userKycFilter]);

  const filteredBets = useMemo(() => {
    const q = betSearch.toLowerCase();
    return bets.filter((b) => {
      if (betFilter !== 'all' && b.status !== betFilter) return false;
      if (!q) return true;
      return (b.bet_description || '').toLowerCase().includes(q)
        || (b.bet_reference || '').toLowerCase().includes(q)
        || (b.username || '').toLowerCase().includes(q);
    });
  }, [bets, betFilter, betSearch]);

  const filteredTx = useMemo(() => {
    const q = txSearch.toLowerCase();
    return transactions.filter((t) => {
      if (txFilter && t.transaction_type !== txFilter) return false;
      if (!q) return true;
      return (t.user_name || '').toLowerCase().includes(q)
        || (t.email || '').toLowerCase().includes(q)
        || (t.bet_reference || '').toLowerCase().includes(q)
        || (t.transaction_id || '').toLowerCase().includes(q);
    });
  }, [transactions, txFilter, txSearch]);

  const filteredRps = useMemo(() => {
    return rpsFilter ? rpsGames.filter((g) => g.status === rpsFilter) : rpsGames;
  }, [rpsGames, rpsFilter]);

  // ----- Actions -----
  const approveBet = async (id) => {
    try { await adminApi.betAction(id, 'approve'); toast.success('Bet approved'); fetchAll(); } catch { toast.error('Failed'); }
  };
  const submitReject = async () => {
    if (!rejectModal || !rejectReason.trim()) return toast.error('Reason required');
    try { await adminApi.betAction(rejectModal, 'decline', rejectReason); toast.success('Bet declined'); setRejectModal(null); setRejectReason(''); fetchAll(); } catch { toast.error('Failed'); }
  };
  const flagBet = async (id) => {
    const c = prompt('Flag reason:') || 'flagged by admin';
    try { await adminApi.betAction(id, 'flag', c); toast.success('Flagged'); fetchAll(); } catch { toast.error('Failed'); }
  };
  const deleteBet = async (id) => {
    if (!window.confirm('Delete this bet permanently?')) return;
    try { await adminApi.betAction(id, 'delete', 'admin delete'); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed'); }
  };

  const submitSuspend = async () => {
    if (!suspendModal) return;
    try { await adminApi.userAction(suspendModal.id, suspendModal.action, suspendReason || '-'); toast.success(`User ${suspendModal.action}`); setSuspendModal(null); setSuspendReason(''); fetchAll(); } catch { toast.error('Failed'); }
  };

  const kycApprove = async (uid) => {
    try { await adminApi.kycApprove(uid, 'ok'); toast.success('KYC approved'); fetchAll(); } catch { toast.error('Failed'); }
  };
  const submitKycReject = async () => {
    if (!kycRejectModal || !kycComment.trim()) return toast.error('Comment required');
    try { await adminApi.kycReject(kycRejectModal, kycComment); toast.success('KYC rejected'); setKycRejectModal(null); setKycComment(''); fetchAll(); } catch { toast.error('Failed'); }
  };

  const openProfile = async (uid) => {
    setProfileModal(uid); setProfileData(null);
    try { const { data } = await adminApi.userProfile(uid); setProfileData(data); } catch { toast.error('Failed to load profile'); }
  };

  const markContact = async (id, status) => {
    try { await adminApi.contactMessageStatus(id, status); fetchAll(); } catch { toast.error('Failed'); }
  };

  const generateAI = async () => {
    try { const { data } = await adminApi.generateAIBets(15); toast.success(`Generated ${data.bets_created} bets, ${data.rps_games_created} RPS`); fetchAll(); } catch { toast.error('Failed'); }
  };
  const creditNPCs = async () => {
    try { const { data } = await adminApi.creditNPCs(500); toast.success(`Credited ${data.npcs_credited} NPCs`); fetchAll(); } catch { toast.error('Failed'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const ov = overview || { users: {}, bets: {}, revenue: {}, platform_profit: {}, rps: {}, moderation: {} };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
      {/* Mobile Block */}
      <div className="md:hidden min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center">
          <Shield className="h-16 w-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>Admin Panel</h1>
          <p className="text-gray-400">The admin control panel is only available on desktop web browsers. Please use a computer to access this page.</p>
        </div>
      </div>

      {/* Desktop Admin */}
      <div className="hidden md:flex md:flex-col md:min-h-screen">
        {/* Admin Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>
                  KrazyBets <span className="text-[#D4AF37]">Super Admin</span>
                </h1>
                <p className="text-xs text-gray-500">Enterprise Control Panel · {user?.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={fetchAll} className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm flex items-center gap-1" data-testid="admin-refresh">
                <RefreshCw className="h-3 w-3" /> Refresh
              </button>
              <span className="text-sm text-gray-600">{user?.full_name}</span>
              <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">{user?.full_name?.charAt(0)}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-1">
          {/* Sidebar */}
          <nav className="w-56 bg-white border-r border-gray-200 py-4 flex-shrink-0 overflow-y-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-colors ${active ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-r-2 border-[#D4AF37]' : 'text-gray-600 hover:bg-gray-50'}`}
                  data-testid={`admin-tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" /> {tab.label}
                </button>
              );
            })}
          </nav>

          <main className="flex-1 p-6 overflow-y-auto min-w-0">
            {/* ========== OVERVIEW ========== */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>Dashboard Overview</h2>
                  <span className="text-xs text-gray-500">Click any card to drill down</span>
                </div>

                {/* Top row: Users / Bets / Revenue / Profit */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard icon={Users} label="Total Users" value={ov.users.total || 0}
                    sub={`${ov.users.active || 0} active · ${ov.users.suspended || 0} suspended`}
                    onClick={() => setActiveTab('users')} />
                  <StatCard icon={Target} label="Total Bets" value={ov.bets.total || 0} color="#28A745"
                    sub={`${ov.bets.live || 0} live · ${ov.bets.pending_approval || 0} pending`}
                    onClick={() => setActiveTab('bets')} />
                  <StatCard icon={DollarSign} label="Revenue (30d)" value={`$${(ov.revenue.monthly || 0).toFixed(0)}`} color="#22C55E"
                    sub={`Today: $${(ov.revenue.daily || 0).toFixed(0)}`}
                    onClick={() => setActiveTab('transactions')} />
                  <StatCard icon={TrendingUp} label="Platform Profit" value={`$${(ov.platform_profit.total_commission || 0).toFixed(0)}`} color="#D4AF37"
                    sub={`Escrow: $${(ov.platform_profit.total_escrow || 0).toFixed(0)}`}
                    onClick={() => setActiveTab('transactions')} />
                </div>

                {/* Second row: Active games / AI alerts / Moderator actions */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard icon={Trophy} label="Active Games" value={ov.rps.active || 0} color="#6366F1"
                    sub={`${ov.rps.total || 0} total RPS`}
                    onClick={() => setActiveTab('rps')} />
                  <StatCard icon={AlertCircle} label="AI Risk Alerts" value={aiAlerts.length} color="#EF4444"
                    sub={`${aiAlerts.filter(a => a.severity === 'high').length} high severity`}
                    onClick={() => setActiveTab('ai-insights')} />
                  <StatCard icon={Activity} label="Moderator Actions Today" value={ov.moderation.actions_today || 0} color="#8B5CF6"
                    sub={`${audit.length} total in log`}
                    onClick={() => setActiveTab('audit')} />
                  <StatCard icon={MessageSquare} label="Open Tickets + Msgs" value={(ov.moderation.open_tickets || 0) + contact.filter(c => c.status === 'new').length} color="#F59E0B"
                    sub={`${contact.filter(c => c.status === 'new').length} new contact`}
                    onClick={() => setActiveTab('contact')} />
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#D4AF37]" /> Growth Trends (Last 30d)</h3>
                    {growth && (
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={growth.labels.map((l, i) => ({
                          date: l.slice(5),
                          Users: growth.users[i],
                          Bets: growth.bets[i],
                          Revenue: growth.revenue[i],
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" fontSize={11} stroke="#6b7280" />
                          <YAxis fontSize={11} stroke="#6b7280" />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="Users" stroke="#D4AF37" strokeWidth={2} />
                          <Line type="monotone" dataKey="Bets" stroke="#28A745" strokeWidth={2} />
                          <Line type="monotone" dataKey="Revenue" stroke="#6366F1" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Globe className="h-4 w-4 text-[#D4AF37]" /> Users by Country</h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {geoStats.slice(0, 10).map((g) => (
                        <div key={g.country} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{g.country}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-100 rounded overflow-hidden">
                              <div className="h-full bg-[#D4AF37]" style={{ width: `${Math.min(100, (g.users / Math.max(1, geoStats[0]?.users)) * 100)}%` }} />
                            </div>
                            <span className="font-bold text-gray-900 w-8 text-right">{g.users}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* AI Alerts preview */}
                {aiAlerts.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><AlertCircle className="h-4 w-4 text-red-500" /> AI Risk Alerts (top 5)</h3>
                    <div className="space-y-2">
                      {aiAlerts.slice(0, 5).map((a, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 text-sm">
                          <div>
                            <span className="font-semibold text-gray-900">{a.title}</span>
                            <span className="text-xs text-gray-500 block">{a.description}</span>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${a.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{a.severity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ========== USERS ========== */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>User Management · {filteredUsers.length}</h2>
                  <Button onClick={() => downloadBlob(adminApi.exportTable('users'), 'users.csv')} className="bg-[#D4AF37] text-white hover:bg-[#C4A037]" data-testid="export-users">
                    <Download className="h-4 w-4 mr-1" /> Export CSV
                  </Button>
                </div>
                {/* Filters */}
                <div className="flex items-center gap-2 flex-wrap bg-white p-3 rounded-xl border border-gray-200">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search name, email, phone..." className="pl-9" data-testid="user-search" />
                  </div>
                  <select value={userStatusFilter} onChange={(e) => setUserStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" data-testid="user-status-filter">
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="banned">Banned</option>
                  </select>
                  <select value={userKycFilter} onChange={(e) => setUserKycFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" data-testid="user-kyc-filter">
                    <option value="">All KYC</option>
                    <option value="not_submitted">Not Submitted</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="text-xs min-w-full">
                    <thead><tr className="border-b bg-gray-50 text-gray-700">
                      {['Name','Surname','Email','Phone','Country','Status','Role','Tier','QBC','Deposits','Withdrawals','Bets','W/L','KYC','Risk','Ref Src','Last Login','IP','Actions'].map(h =>
                        <th key={h} className="text-left py-2 px-2 font-bold whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {filteredUsers.slice(0, 100).map((u) => (
                        <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                          <td className="py-2 px-2 font-semibold text-gray-900">{u.name || '-'}</td>
                          <td className="py-2 px-2 text-gray-700">{u.surname || '-'}</td>
                          <td className="py-2 px-2 text-gray-600 max-w-[160px] truncate">{u.email}</td>
                          <td className="py-2 px-2 text-gray-600">{u.mobile_number}</td>
                          <td className="py-2 px-2 text-gray-600">{u.country}</td>
                          <td className="py-2 px-2"><StatusPill status={u.account_status} /></td>
                          <td className="py-2 px-2"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-bold">{u.role}</span></td>
                          <td className="py-2 px-2">{u.subscription_tier}</td>
                          <td className="py-2 px-2 font-bold text-[#D4AF37]">{u.wallet_balance}</td>
                          <td className="py-2 px-2 text-green-700">${u.total_deposits}</td>
                          <td className="py-2 px-2 text-red-600">${u.total_withdrawals}</td>
                          <td className="py-2 px-2">{u.total_bets_placed}</td>
                          <td className="py-2 px-2">{u.total_wins}/{u.total_losses}</td>
                          <td className="py-2 px-2"><StatusPill status={u.kyc_status} /></td>
                          <td className="py-2 px-2"><RiskBadge score={u.risk_score} /></td>
                          <td className="py-2 px-2 text-gray-500">{u.referral_source}</td>
                          <td className="py-2 px-2 text-gray-500 text-[10px]">{u.last_login?.slice(0, 10) || '-'}</td>
                          <td className="py-2 px-2 text-gray-500 text-[10px] max-w-[120px] truncate">{u.ip_address}</td>
                          <td className="py-2 px-2">
                            <div className="flex gap-1">
                              <button onClick={() => openProfile(u.id)} className="p-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200" title="View profile" data-testid={`user-profile-${u.id}`}><Eye className="h-3 w-3" /></button>
                              {u.account_status !== 'suspended' ? (
                                <button onClick={() => setSuspendModal({ id: u.id, action: 'suspend' })} className="p-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200" title="Suspend"><Pause className="h-3 w-3" /></button>
                              ) : (
                                <button onClick={() => adminApi.userAction(u.id, 'unsuspend').then(() => { toast.success('Unsuspended'); fetchAll(); })} className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200" title="Unsuspend"><UserCheck className="h-3 w-3" /></button>
                              )}
                              {!u.flagged ? (
                                <button onClick={() => setSuspendModal({ id: u.id, action: 'flag' })} className="p-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200" title="Flag"><Flag className="h-3 w-3" /></button>
                              ) : (
                                <button onClick={() => adminApi.userAction(u.id, 'unflag').then(() => { toast.success('Unflagged'); fetchAll(); })} className="p-1 bg-gray-100 text-gray-700 rounded" title="Unflag"><Flag className="h-3 w-3" /></button>
                              )}
                              <button onClick={() => setSuspendModal({ id: u.id, action: 'ban' })} className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200" title="Ban"><UserX className="h-3 w-3" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ========== BETS ========== */}
            {activeTab === 'bets' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>Bet Management · {filteredBets.length}</h2>
                  <Button onClick={() => downloadBlob(adminApi.exportTable('bets'), 'bets.csv')} className="bg-[#D4AF37] text-white" data-testid="export-bets">
                    <Download className="h-4 w-4 mr-1" /> Export CSV
                  </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap bg-white p-3 rounded-xl border border-gray-200">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input value={betSearch} onChange={(e) => setBetSearch(e.target.value)} placeholder="Search description, ref, username..." className="pl-9" data-testid="bet-search" />
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {['all','pending_approval','open','active','resolved','cancelled','disputed'].map(f => (
                      <button key={f} onClick={() => setBetFilter(f)} className={`px-3 py-1 rounded-lg text-xs font-semibold ${betFilter === f ? 'bg-[#D4AF37] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`bet-filter-${f}`}>
                        {f.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="text-xs min-w-full">
                    <thead><tr className="border-b bg-gray-50 text-gray-700">
                      {['Ref #','Date','Username','Description','Initiator','Category','Stake','Total Bets','Wins','Losses','Spent','Current','Time Left','Mod','Risk','Status','Actions'].map(h =>
                        <th key={h} className="text-left py-2 px-2 font-bold whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {filteredBets.slice(0, 100).map((b) => (
                        <tr key={b.bet_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`bet-row-${b.bet_id}`}>
                          <td className="py-2 px-2 font-mono text-[#D4AF37] whitespace-nowrap">{b.bet_reference}</td>
                          <td className="py-2 px-2 text-gray-500 text-[10px]">{b.bet_date?.slice(0, 10)}</td>
                          <td className="py-2 px-2 text-gray-700">{b.username}</td>
                          <td className="py-2 px-2 text-gray-900 max-w-[220px] truncate">{b.bet_description}</td>
                          <td className="py-2 px-2 text-gray-700">{b.initiator}</td>
                          <td className="py-2 px-2 text-gray-600">{b.category}</td>
                          <td className="py-2 px-2 font-bold text-[#D4AF37]">{b.stake_amount} {b.stake_type === 'qbean' ? 'QBC' : 'USD'}</td>
                          <td className="py-2 px-2">{b.total_bets}</td>
                          <td className="py-2 px-2 text-green-700">{b.total_wins}</td>
                          <td className="py-2 px-2 text-red-600">{b.total_losses}</td>
                          <td className="py-2 px-2 text-gray-700">${b.total_amount_spent}</td>
                          <td className="py-2 px-2 font-bold text-gray-900">{b.current_bet_amount}</td>
                          <td className="py-2 px-2"><Countdown seconds={b.time_remaining_s} /></td>
                          <td className="py-2 px-2 text-gray-500">{b.moderator_id ? b.moderator_id.slice(-6) : '-'}</td>
                          <td className="py-2 px-2"><RiskBadge score={b.risk_score} /></td>
                          <td className="py-2 px-2"><StatusPill status={b.status} /></td>
                          <td className="py-2 px-2">
                            <div className="flex gap-1 flex-wrap">
                              {b.status === 'pending_approval' && (
                                <>
                                  <button onClick={() => approveBet(b.bet_id)} className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200" title="Approve" data-testid={`approve-${b.bet_id}`}><Check className="h-3 w-3" /></button>
                                  <button onClick={() => setRejectModal(b.bet_id)} className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200" title="Decline (with reason)"><X className="h-3 w-3" /></button>
                                </>
                              )}
                              <button onClick={() => flagBet(b.bet_id)} className="p-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200" title="Flag"><Flag className="h-3 w-3" /></button>
                              <button onClick={() => deleteBet(b.bet_id)} className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200" title="Delete"><Trash2 className="h-3 w-3" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ========== TRANSACTIONS ========== */}
            {activeTab === 'transactions' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>Transactions · {filteredTx.length}</h2>
                  <Button onClick={() => downloadBlob(adminApi.exportTable('transactions'), 'transactions.csv')} className="bg-[#D4AF37] text-white" data-testid="export-tx">
                    <Download className="h-4 w-4 mr-1" /> Export CSV
                  </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap bg-white p-3 rounded-xl border border-gray-200">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input value={txSearch} onChange={(e) => setTxSearch(e.target.value)} placeholder="Search name, email, bet #..." className="pl-9" />
                  </div>
                  <select value={txFilter} onChange={(e) => setTxFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">
                    <option value="">All types</option>
                    <option value="deposit">Deposit</option>
                    <option value="withdrawal">Withdrawal</option>
                    <option value="bet_stake">Bet Stake</option>
                    <option value="bet_win">Win</option>
                    <option value="commission">Commission</option>
                    <option value="admin_topup">Admin Top-up</option>
                  </select>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="text-xs min-w-full">
                    <thead><tr className="border-b bg-gray-50 text-gray-700">
                      {['TX ID','User','Email','Phone','Bet Ref','Type','Amount','Currency','Before','After','Method','Status','Time','Country','IP','Device','Admin','Fraud'].map(h =>
                        <th key={h} className="text-left py-2 px-2 font-bold whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {filteredTx.slice(0, 100).map((t) => (
                        <tr key={t.transaction_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-2 font-mono text-[10px] text-gray-500">{(t.transaction_id || '').slice(0, 8)}</td>
                          <td className="py-2 px-2 font-semibold text-gray-900 max-w-[120px] truncate">{t.user_name}</td>
                          <td className="py-2 px-2 text-gray-600 max-w-[140px] truncate">{t.email}</td>
                          <td className="py-2 px-2 text-gray-600">{t.mobile_number}</td>
                          <td className="py-2 px-2 font-mono text-[#D4AF37] text-[10px]">{t.bet_reference}</td>
                          <td className="py-2 px-2"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px] font-bold">{t.transaction_type}</span></td>
                          <td className={`py-2 px-2 font-bold ${t.amount > 0 ? 'text-green-600' : 'text-red-500'}`}>{t.amount > 0 ? '+' : ''}{t.amount}</td>
                          <td className="py-2 px-2 text-gray-500">{t.currency}</td>
                          <td className="py-2 px-2 text-gray-500">{t.wallet_before}</td>
                          <td className="py-2 px-2 text-gray-500">{t.wallet_after}</td>
                          <td className="py-2 px-2 text-gray-600">{t.payment_method}</td>
                          <td className="py-2 px-2"><StatusPill status={t.status} /></td>
                          <td className="py-2 px-2 text-gray-500 text-[10px]">{t.timestamp?.slice(0, 16).replace('T', ' ')}</td>
                          <td className="py-2 px-2 text-gray-500">{t.country}</td>
                          <td className="py-2 px-2 text-gray-500 text-[10px] max-w-[100px] truncate">{t.ip_address}</td>
                          <td className="py-2 px-2 text-gray-500 text-[10px] max-w-[100px] truncate">{t.device_used}</td>
                          <td className="py-2 px-2 text-gray-500">{t.admin_action}</td>
                          <td className="py-2 px-2">{t.fraud_flag ? <Flag className="h-3 w-3 text-red-600" /> : <span className="text-gray-300">-</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ========== RPS ========== */}
            {activeTab === 'rps' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>RPS Games · {filteredRps.length}</h2>
                  <Button onClick={() => downloadBlob(adminApi.exportTable('rps'), 'rps_games.csv')} className="bg-[#D4AF37] text-white" data-testid="export-rps">
                    <Download className="h-4 w-4 mr-1" /> Export CSV
                  </Button>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {['','active','completed','pending','cancelled','expired'].map(f => (
                    <button key={f || 'all'} onClick={() => setRpsFilter(f)} className={`px-3 py-1 rounded-lg text-xs font-semibold ${rpsFilter === f ? 'bg-[#D4AF37] text-white' : 'bg-gray-100 text-gray-600'}`}>
                      {f || 'All'}
                    </button>
                  ))}
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="text-xs min-w-full">
                    <thead><tr className="border-b bg-gray-50 text-gray-700">
                      {['Game ID','Bet Ref','Player A','Player B','Stake','Status','Winner','Loser','Date','Duration','Commission','Moderator','Dispute','AI Fair'].map(h =>
                        <th key={h} className="text-left py-2 px-2 font-bold whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {filteredRps.slice(0, 100).map((g) => (
                        <tr key={g.game_id} className="border-b border-gray-100">
                          <td className="py-2 px-2 font-mono text-[10px] text-gray-500">{(g.game_id || '').slice(0, 8)}</td>
                          <td className="py-2 px-2 font-mono text-[#D4AF37] text-[10px]">{(g.bet_reference || '').slice(0, 10)}</td>
                          <td className="py-2 px-2 font-semibold text-gray-900">{g.player_a}</td>
                          <td className="py-2 px-2 font-semibold text-gray-900">{g.player_b}</td>
                          <td className="py-2 px-2 font-bold text-[#D4AF37]">{g.bet_amount}</td>
                          <td className="py-2 px-2"><StatusPill status={g.status} /></td>
                          <td className="py-2 px-2 text-green-700 font-semibold">{g.winner}</td>
                          <td className="py-2 px-2 text-red-600">{g.loser}</td>
                          <td className="py-2 px-2 text-gray-500 text-[10px]">{g.game_date?.slice(0, 10)}</td>
                          <td className="py-2 px-2 text-gray-600">{g.duration_s ? `${g.duration_s}s` : '-'}</td>
                          <td className="py-2 px-2 text-green-700">${g.commission_earned}</td>
                          <td className="py-2 px-2 text-gray-600">{g.moderator}</td>
                          <td className="py-2 px-2">{g.dispute_status}</td>
                          <td className="py-2 px-2 text-gray-700 font-bold">{g.ai_fairness_score}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ========== KYC ========== */}
            {activeTab === 'kyc' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>KYC Panel · {kycList.length}</h2>
                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="text-sm min-w-full">
                    <thead><tr className="border-b bg-gray-50 text-gray-700">
                      {['User','Email','Country','Phone','Submitted','Status','Duplicate','Actions'].map(h =>
                        <th key={h} className="text-left py-2 px-3 font-bold">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {kycList.map((u) => (
                        <tr key={u.id} className="border-b border-gray-100">
                          <td className="py-2 px-3 font-semibold text-gray-900">{u.full_name}</td>
                          <td className="py-2 px-3 text-gray-600">{u.email}</td>
                          <td className="py-2 px-3 text-gray-600">{u.country || '-'}</td>
                          <td className="py-2 px-3 text-gray-600">{u.mobile_number || '-'}</td>
                          <td className="py-2 px-3 text-gray-500 text-xs">{u.kyc_submitted_at?.slice(0, 10) || '-'}</td>
                          <td className="py-2 px-3"><StatusPill status={u.kyc_status} /></td>
                          <td className="py-2 px-3">{u.duplicate_flag ? <span className="text-red-600 font-bold text-xs">⚠ Duplicate</span> : <span className="text-gray-400 text-xs">clean</span>}</td>
                          <td className="py-2 px-3">
                            {u.kyc_status !== 'approved' && (
                              <div className="flex gap-1">
                                <button onClick={() => kycApprove(u.id)} className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200" title="Approve"><Check className="h-3 w-3" /></button>
                                <button onClick={() => setKycRejectModal(u.id)} className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200" title="Reject (comment required)"><X className="h-3 w-3" /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {kycList.length === 0 && (
                        <tr><td colSpan="8" className="py-8 text-center text-gray-500">No KYC submissions yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ========== AI INSIGHTS ========== */}
            {activeTab === 'ai-insights' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>AI Insights & Risk Alerts</h2>
                <div className="grid grid-cols-3 gap-3">
                  <StatCard icon={AlertCircle} label="High severity" value={aiAlerts.filter(a => a.severity === 'high').length} color="#EF4444" />
                  <StatCard icon={AlertCircle} label="Medium" value={aiAlerts.filter(a => a.severity === 'medium').length} color="#F59E0B" />
                  <StatCard icon={AlertCircle} label="Total Alerts" value={aiAlerts.length} color="#D4AF37" />
                </div>
                {aiAlerts.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
                    <Brain className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    No active risk alerts. The AI engine is continuously monitoring user behaviour, bet spikes and duplicate identities.
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {aiAlerts.map((a, i) => (
                      <div key={i} className="p-4 flex items-start gap-3" data-testid={`ai-alert-${i}`}>
                        <div className={`w-2 h-2 rounded-full mt-2 ${a.severity === 'high' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">{a.title}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${a.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{a.severity}</span>
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-xs text-gray-600">{a.type}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{a.description}</p>
                        </div>
                        {a.target_type === 'user' && a.target_id && (
                          <button onClick={() => { setActiveTab('users'); openProfile(a.target_id); }} className="px-3 py-1 rounded bg-[#D4AF37] text-white text-xs font-semibold">Investigate</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ========== LEADERBOARD ========== */}
            {activeTab === 'leaderboard' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>Leaderboards</h2>
                <div className="flex gap-2">
                  {['wins','losses','reputation'].map(m => (
                    <button key={m} onClick={() => setLbMetric(m)} className={`px-3 py-1 rounded-lg text-sm font-semibold ${lbMetric === m ? 'bg-[#D4AF37] text-white' : 'bg-gray-100 text-gray-600'}`}>
                      Top by {m}
                    </button>
                  ))}
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b bg-gray-50">
                      {['#','User','Country','Tier','Wins','Losses','Reputation','QBC'].map(h => <th key={h} className="text-left py-2 px-3 font-bold text-gray-700">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {leaderboard.map((u, i) => (
                        <tr key={u.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 font-black text-[#D4AF37]">#{i + 1}</td>
                          <td className="py-2 px-3 font-semibold text-gray-900">{u.name}</td>
                          <td className="py-2 px-3 text-gray-600">{u.country}</td>
                          <td className="py-2 px-3">{u.tier}</td>
                          <td className="py-2 px-3 text-green-700">{u.wins}</td>
                          <td className="py-2 px-3 text-red-600">{u.losses}</td>
                          <td className="py-2 px-3">{u.reputation}</td>
                          <td className="py-2 px-3 font-bold text-[#D4AF37]">{u.qbc_balance}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ========== AUDIT LOG ========== */}
            {activeTab === 'audit' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>Admin Audit Log · {audit.length}</h2>
                  <Button onClick={() => downloadBlob(adminApi.exportTable('audit'), 'audit_log.csv')} className="bg-[#D4AF37] text-white">
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="text-xs min-w-full">
                    <thead><tr className="border-b bg-gray-50 text-gray-700">
                      {['Time','Actor','Role','Action','Target Type','Target','IP','Details'].map(h =>
                        <th key={h} className="text-left py-2 px-2 font-bold whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {audit.map((l) => (
                        <tr key={l.id} className="border-b border-gray-100">
                          <td className="py-2 px-2 text-gray-500 text-[10px]">{l.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                          <td className="py-2 px-2 font-semibold text-gray-900">{l.actor_name}</td>
                          <td className="py-2 px-2 text-gray-600">{l.actor_role}</td>
                          <td className="py-2 px-2"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px] font-bold">{l.action}</span></td>
                          <td className="py-2 px-2 text-gray-600">{l.target_type}</td>
                          <td className="py-2 px-2 font-mono text-gray-500 text-[10px]">{(l.target_id || '').slice(0, 10)}</td>
                          <td className="py-2 px-2 text-gray-500 text-[10px]">{l.ip_address}</td>
                          <td className="py-2 px-2 text-gray-600 max-w-[240px] truncate">{JSON.stringify(l.details)}</td>
                        </tr>
                      ))}
                      {audit.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-gray-500">No admin actions logged yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ========== CONTACT MESSAGES ========== */}
            {activeTab === 'contact' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>Contact Messages · {contact.length}</h2>
                {contact.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
                    <Mail className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    No contact form submissions yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contact.map((m) => (
                      <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`contact-msg-${m.id}`}>
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <div>
                            <span className="font-bold text-gray-900">{m.full_name}</span>
                            <span className="text-gray-500 text-sm"> · {m.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusPill status={m.status} />
                            <span className="text-xs text-gray-400">{m.created_at?.slice(0, 16).replace('T', ' ')}</span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">Subject: <span className="font-semibold text-gray-700">{m.subject}</span> · IP: {m.ip_address}</div>
                        <p className="text-gray-800 text-sm whitespace-pre-wrap">{m.message}</p>
                        <div className="flex gap-2 mt-3">
                          {m.status === 'new' && <Button onClick={() => markContact(m.id, 'read')} variant="outline" className="text-xs">Mark as read</Button>}
                          {m.status !== 'replied' && <Button onClick={() => markContact(m.id, 'replied')} className="bg-[#28A745] text-white text-xs">Mark replied</Button>}
                          <a href={`mailto:${m.email}?subject=Re: ${m.subject}`} className="px-3 py-1 rounded bg-[#D4AF37] text-white text-xs font-semibold">Reply via email</a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ========== SUPPORT ========== */}
            {activeTab === 'support' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>Support Tickets</h2>
                {support.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                    <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No support tickets yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {support.map((t) => (
                      <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <StatusPill status={t.status} />
                          <span className="text-xs text-gray-400">{new Date(t.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-800">{t.message}</p>
                        <p className="text-xs text-gray-500 mt-1">Session: {t.session_id}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ========== AI ENGINE ========== */}
            {activeTab === 'ai-engine' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Outfit' }}>AI Engine Controls</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <Bot className="h-8 w-8 text-[#D4AF37] mb-3" />
                    <h3 className="font-bold text-gray-900 mb-2">Generate AI Bets</h3>
                    <p className="text-sm text-gray-500 mb-4">Create realistic NPC bets across all categories with auto-matching.</p>
                    <Button onClick={generateAI} className="bg-[#D4AF37] text-white hover:bg-[#C4A037] w-full" data-testid="generate-ai-bets">
                      <RefreshCw className="h-4 w-4 mr-2" /> Generate 15 AI Bets + 3 RPS Games
                    </Button>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <Wallet className="h-8 w-8 text-[#28A745] mb-3" />
                    <h3 className="font-bold text-gray-900 mb-2">Credit NPC Wallets</h3>
                    <p className="text-sm text-gray-500 mb-4">Give all NPC accounts 500 QBC to keep the ecosystem active.</p>
                    <Button onClick={creditNPCs} className="bg-[#28A745] text-white hover:bg-[#1E8C3A] w-full" data-testid="credit-npcs">
                      <DollarSign className="h-4 w-4 mr-2" /> Credit 500 QBC to All NPCs
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>

        {/* Reject Bet Modal */}
        {rejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" data-testid="reject-modal">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Decline Bet</h2>
              <p className="text-xs text-gray-500 mb-3">A reason is required and will be logged to audit.</p>
              <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejection..." className="mb-4" data-testid="reject-reason" />
              <div className="flex gap-2">
                <Button onClick={() => { setRejectModal(null); setRejectReason(''); }} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={submitReject} className="bg-red-500 text-white flex-1" disabled={!rejectReason.trim()} data-testid="reject-submit">Decline</Button>
              </div>
            </div>
          </div>
        )}

        {/* Suspend/Flag/Ban Modal */}
        {suspendModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
              <h2 className="text-lg font-bold text-gray-900 mb-2 capitalize">{suspendModal.action} User</h2>
              <p className="text-xs text-gray-500 mb-3">A reason will be logged to the audit trail.</p>
              <Input value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} placeholder="Reason..." className="mb-4" />
              <div className="flex gap-2">
                <Button onClick={() => { setSuspendModal(null); setSuspendReason(''); }} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={submitSuspend} className="bg-red-500 text-white flex-1 capitalize">{suspendModal.action}</Button>
              </div>
            </div>
          </div>
        )}

        {/* KYC Reject Modal */}
        {kycRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Reject KYC</h2>
              <p className="text-xs text-gray-500 mb-3">A comment is mandatory and will be shown to the user.</p>
              <Input value={kycComment} onChange={(e) => setKycComment(e.target.value)} placeholder="Comment (e.g. blurry ID)..." className="mb-4" />
              <div className="flex gap-2">
                <Button onClick={() => { setKycRejectModal(null); setKycComment(''); }} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={submitKycReject} className="bg-red-500 text-white flex-1" disabled={!kycComment.trim()}>Reject</Button>
              </div>
            </div>
          </div>
        )}

        {/* User Profile Modal */}
        {profileModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-black text-gray-900">User Profile</h2>
                <button onClick={() => { setProfileModal(null); setProfileData(null); }} className="p-1 rounded hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-6">
                {!profileData ? (
                  <div className="py-8 text-center text-gray-500"><div className="w-8 h-8 mx-auto border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Name:</span> <span className="font-bold">{profileData.user.full_name}</span></div>
                      <div><span className="text-gray-500">Email:</span> <span>{profileData.user.email}</span></div>
                      <div><span className="text-gray-500">Country:</span> <span>{profileData.user.country}</span></div>
                      <div><span className="text-gray-500">Phone:</span> <span>{profileData.user.mobile_number || '-'}</span></div>
                      <div><span className="text-gray-500">Registered:</span> <span>{profileData.user.created_at?.slice(0, 10)}</span></div>
                      <div><span className="text-gray-500">Last login:</span> <span>{profileData.user.last_login?.slice(0, 16).replace('T', ' ') || '-'}</span></div>
                      <div><span className="text-gray-500">Last IP:</span> <span>{profileData.user.last_ip}</span></div>
                      <div><span className="text-gray-500">Device:</span> <span className="truncate">{profileData.user.last_device?.slice(0, 40)}</span></div>
                      <div><span className="text-gray-500">Role:</span> <span className="font-semibold">{profileData.user.role}</span></div>
                      <div><span className="text-gray-500">Tier:</span> <span>{profileData.user.subscription_tier}</span></div>
                      <div><span className="text-gray-500">Status:</span> <StatusPill status={profileData.user.account_status} /></div>
                      <div><span className="text-gray-500">KYC:</span> <StatusPill status={profileData.user.kyc_status} /></div>
                      <div><span className="text-gray-500">Wins:</span> <span className="text-green-700 font-bold">{profileData.user.wins || 0}</span></div>
                      <div><span className="text-gray-500">Losses:</span> <span className="text-red-600 font-bold">{profileData.user.losses || 0}</span></div>
                      <div><span className="text-gray-500">QBC Balance:</span> <span className="font-bold text-[#D4AF37]">{profileData.wallet.qbean_balance || 0}</span></div>
                      <div><span className="text-gray-500">Escrow Locked:</span> <span>{profileData.wallet.escrow_locked || 0}</span></div>
                    </div>
                    <div>
                      <h3 className="font-bold text-sm mb-2">Recent bets ({profileData.recent_bets.length})</h3>
                      <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                        {profileData.recent_bets.slice(0, 10).map((b) => (
                          <div key={b.id} className="p-2 flex items-center justify-between text-xs">
                            <span className="truncate flex-1">{b.title}</span>
                            <StatusPill status={b.status} />
                          </div>
                        ))}
                        {profileData.recent_bets.length === 0 && <div className="p-3 text-center text-gray-400 text-xs">No bets</div>}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-sm mb-2">Recent transactions ({profileData.recent_transactions.length})</h3>
                      <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                        {profileData.recent_transactions.slice(0, 10).map((t) => (
                          <div key={t.id} className="p-2 flex items-center justify-between text-xs">
                            <span className="text-gray-500">{t.type}</span>
                            <span className={`font-bold ${t.amount > 0 ? 'text-green-600' : 'text-red-500'}`}>{t.amount > 0 ? '+' : ''}{t.amount}</span>
                          </div>
                        ))}
                        {profileData.recent_transactions.length === 0 && <div className="p-3 text-center text-gray-400 text-xs">No transactions</div>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
